// GtInterviewRoom: the candidate's interview with Nathan, at /interview/:token.
//
// Public: no sign-in, the link is the key. Steps: what happens and agreement,
// a camera and microphone check, the live call, then done. A dropped call or a
// pause resumes from the same link until it expires.
//
// The call runs on LiveKit. Nathan's voice drives the orb; both sides are
// transcribed live (Nathan's words arrive in step with his voice). The browser
// records the call (the candidate's camera, both voices) and uploads it in
// pieces as it goes, so a closed tab loses at most a few seconds.
import { useState, useEffect, useRef, useCallback } from 'react';
import { Room, RoomEvent, Track, createLocalTracks, VideoPresets } from 'livekit-client';
import API_BASE from '../../config';

const TOPIC = 'lk.transcription';
const PIECE_MS = 8000;

function ukDay(v) {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}
function clock(s) {
  const m = Math.floor(s / 60); const r = s % 60;
  return (m < 10 ? '0' : '') + m + ':' + (r < 10 ? '0' : '') + r;
}
async function call(token, path, body) {
  const res = await fetch(API_BASE + '/api/public/interviews/' + token + (path || ''), body === undefined ? {} : {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(j.message || 'Something went wrong.'); e.status = res.status; e.code = j.error; throw e; }
  return j;
}
function pickMime() {
  if (typeof window === 'undefined' || !window.MediaRecorder) return null;
  const options = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm', 'video/mp4;codecs=avc1,mp4a', 'video/mp4'];
  return options.find((m) => { try { return window.MediaRecorder.isTypeSupported(m); } catch (e) { return false; } }) || null;
}
function levelOf(analyser, buf) {
  if (!analyser) return 0;
  analyser.getByteTimeDomainData(buf);
  let sum = 0;
  for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
  return Math.min(1, Math.sqrt(sum / buf.length) * 4);
}

// ── The orb ────────────────────────────────────────────────────────────────
function Orb({ levelRef, modeRef }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return undefined;
    const ctx = cv.getContext('2d');
    const W = cv.width; const H = cv.height; const CX = W / 2; const CY = H / 2;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const t0 = performance.now();
    let amp = 0; let raf = 0;
    function blob(r, wobble, t, phase, a, b, alpha) {
      const g = ctx.createRadialGradient(CX - r * 0.25, CY - r * 0.3, r * 0.1, CX, CY, r * 1.15);
      g.addColorStop(0, a); g.addColorStop(1, b);
      ctx.beginPath();
      for (let i = 0; i <= 120; i++) {
        const ang = (i / 120) * Math.PI * 2;
        const d = 1 + wobble * 0.55 * Math.sin(3 * ang + t * 1.7 + phase) + wobble * 0.35 * Math.sin(5 * ang - t * 2.3 + phase * 2) + wobble * 0.22 * Math.sin(7 * ang + t * 3.1 - phase);
        const x = CX + Math.cos(ang) * r * d; const y = CY + Math.sin(ang) * r * d;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.globalAlpha = alpha; ctx.fillStyle = g; ctx.fill(); ctx.globalAlpha = 1;
    }
    function frame(now) {
      const t = (now - t0) / 1000;
      const mode = modeRef.current;
      const target = mode === 'speaking' ? Math.max(0.15, levelRef.current) : 0;
      amp += (target - amp) * 0.22;
      const k = reduce ? 0.35 : 1;
      ctx.clearRect(0, 0, W, H);
      const breathe = mode === 'listening' ? Math.sin(t * 1.4) * 0.025 : 0;
      const base = W * (0.2 + breathe * 0.8 + amp * 0.04 * k);
      const wob = (mode === 'speaking' ? 0.025 + amp * 0.08 : mode === 'thinking' ? 0.03 : 0.018) * k;
      const glowR = Math.min(base * 1.9, W * 0.48);
      const glow = ctx.createRadialGradient(CX, CY, base * 0.6, CX, CY, glowR);
      glow.addColorStop(0, 'rgba(31,169,178,' + (0.22 + amp * 0.25) + ')');
      glow.addColorStop(1, 'rgba(31,169,178,0)');
      ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(CX, CY, glowR, 0, Math.PI * 2); ctx.fill();
      ctx.globalCompositeOperation = 'lighter';
      blob(base * 1.04, wob * 1.2, t * 0.8, 0, 'rgba(31,111,185,0.9)', 'rgba(16,40,70,0)', 0.75);
      blob(base * 0.98, wob, t, 1.7, 'rgba(25,190,190,0.9)', 'rgba(10,70,80,0)', 0.8);
      blob(base * 0.86, wob * 0.8, t * 1.25, 3.1, 'rgba(255,170,130,0.55)', 'rgba(120,60,40,0)', 0.45);
      ctx.globalCompositeOperation = 'source-over';
      const hl = ctx.createRadialGradient(CX - base * 0.35, CY - base * 0.45, 0, CX - base * 0.35, CY - base * 0.45, base * 0.7);
      hl.addColorStop(0, 'rgba(255,255,255,0.28)'); hl.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = hl; ctx.beginPath(); ctx.arc(CX, CY, base * 0.98, 0, Math.PI * 2); ctx.fill();
      if (mode === 'thinking') {
        for (let i = 0; i < 14; i++) {
          const a = t * 1.6 + (i / 14) * Math.PI * 2;
          const r = base * 1.22 + Math.sin(t * 3 + i) * 6;
          ctx.globalAlpha = 0.25 + 0.6 * (0.5 + 0.5 * Math.sin(t * 4 - i * 0.6));
          ctx.fillStyle = '#7FD8DC';
          ctx.beginPath(); ctx.arc(CX + Math.cos(a) * r, CY + Math.sin(a) * r, 4, 0, Math.PI * 2); ctx.fill();
        }
        ctx.globalAlpha = 1;
      }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [levelRef, modeRef]);
  return <canvas ref={ref} className="gtir-orb" width="720" height="720" aria-hidden="true" />;
}

// Five bars that move with the candidate's own microphone.
function MicMeter({ levelRef, muted }) {
  const ref = useRef(null);
  useEffect(() => {
    let raf = 0;
    function frame() {
      const el = ref.current;
      if (el) {
        const v = muted ? 0 : levelRef.current;
        Array.from(el.children).forEach((b, i) => {
          const h = 3 + Math.max(0, Math.min(1, v * (1.4 - Math.abs(i - 2) * 0.25))) * 13;
          b.style.height = h.toFixed(1) + 'px';
        });
      }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [levelRef, muted]);
  return <div className="gtir-lvl" ref={ref} aria-hidden="true"><span /><span /><span /><span /><span /></div>;
}

function Brand({ title }) {
  return (
    <div className="gtir-brand">
      <img src="/favicon-512.png" alt="" className="gtir-mark" />
      <div><b>{title || 'Interview'}</b><span>Generative Talent Co</span></div>
    </div>
  );
}

function Shell({ title, children, wide }) {
  return (
    <div className="gtir">
      <div className={'gtir-wrap' + (wide ? ' gtir-wrap--wide' : '')}>
        <header className="gtir-top"><Brand title={title} /></header>
        {children}
      </div>
    </div>
  );
}

function Message({ title, heading, children, action }) {
  return (
    <Shell title={title}>
      <section className="gtir-card gtir-msg">
        <span className="gtir-orbmini" aria-hidden="true" />
        <h1>{heading}</h1>
        {children}
        {action}
      </section>
    </Shell>
  );
}

export default function GtInterviewRoom({ token }) {
  const [info, setInfo] = useState(null);
  const [stage, setStage] = useState('loading'); // loading notice check call done paused dropped closed gone error
  const [error, setError] = useState('');
  const [agree, setAgree] = useState(false);
  const [clipsOk, setClipsOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deviceError, setDeviceError] = useState('');
  const [segments, setSegments] = useState([]);
  const [agentState, setAgentState] = useState('initializing');
  const [progress, setProgress] = useState({ q: 0, total: 0 });
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [needsAudio, setNeedsAudio] = useState(false);
  const [nathanLate, setNathanLate] = useState(false);
  const [showTx, setShowTx] = useState(false);
  const [devicesReady, setDevicesReady] = useState(false);
  const [strikeWarn, setStrikeWarn] = useState(false);
  const [needFs, setNeedFs] = useState(false);
  const strikes = useRef(0);
  const awaySince = useRef(0);

  const roomRef = useRef(null);
  const tracksRef = useRef([]);
  const previewRef = useRef(null);
  const selfRef = useRef(null);
  const audioBoxRef = useRef(null);
  const txEndRef = useRef(null);
  const acRef = useRef(null);
  const micAnalyser = useRef(null);
  const nathanAnalyser = useRef(null);
  const mixRef = useRef(null);
  const micLevel = useRef(0);
  const nathanLevel = useRef(0);
  const modeRef = useRef('listening');
  const recRef = useRef(null);
  const uploadRef = useRef({ session: 0, seq: 0, queue: Promise.resolve(), mime: null });
  const leavingRef = useRef(null);
  const devGen = useRef(0);
  const [devices, setDevices] = useState({ audioinput: [], videoinput: [], audiooutput: [] });
  const [chosen, setChosen] = useState({ audioinput: '', videoinput: '', audiooutput: '' });
  const [tone, setTone] = useState(false);

  const load = useCallback(async () => {
    try {
      const v = await call(token);
      setInfo(v);
      return v;
    } catch (e) {
      if (e.status === 404) setStage('gone'); else { setError(e.message); setStage('error'); }
      return null;
    }
  }, [token]);

  useEffect(() => {
    let live = true;
    (async () => {
      const v = await load();
      if (!live || !v) return;
      if (v.status === 'completed') setStage('done');
      else if (v.status === 'expired' || v.status === 'cancelled') setStage('closed');
      else setStage(v.consent_given ? 'check' : 'notice');
    })();
    return () => { live = false; };
  }, [load]);

  // One loop reads both voice levels for the orb and the meter.
  useEffect(() => {
    let raf = 0;
    const buf = new Uint8Array(1024);
    function frame() {
      micLevel.current = micLevel.current * 0.6 + levelOf(micAnalyser.current, buf) * 0.4;
      const n = levelOf(nathanAnalyser.current, buf);
      nathanLevel.current = nathanLevel.current * 0.5 + n * 0.5;
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    modeRef.current = agentState === 'speaking' ? 'speaking' : agentState === 'thinking' ? 'thinking' : 'listening';
  }, [agentState]);

  useEffect(() => {
    if (stage !== 'call') return undefined;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [stage]);

  useEffect(() => { if (txEndRef.current) txEndRef.current.scrollIntoView({ block: 'end' }); }, [segments]);

  // Leaving the page mid-call asks first.
  useEffect(() => {
    if (stage !== 'call') return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [stage]);

  const audioContext = useCallback(() => {
    if (!acRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      acRef.current = AC ? new AC() : null;
    }
    if (acRef.current && acRef.current.state === 'suspended') acRef.current.resume().catch(() => {});
    return acRef.current;
  }, []);

  const stopTracks = useCallback(() => {
    tracksRef.current.forEach((t) => { try { t.stop(); } catch (e) { /* gone */ } });
    tracksRef.current = [];
    micAnalyser.current = null;
    setDevicesReady(false);
  }, []);

  // ── Camera and microphone check ─────────────────────────────────────────
  const startDevices = useCallback(async (pick) => {
    const gen = ++devGen.current;
    const want = pick || {};
    setDeviceError('');
    stopTracks();
    try {
      const tracks = await createLocalTracks({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, ...(want.audioinput ? { deviceId: want.audioinput } : {}) },
        video: { resolution: VideoPresets.h360.resolution, ...(want.videoinput ? { deviceId: want.videoinput } : { facingMode: 'user' }) },
      });
      // A newer start (a device change, or React starting twice) wins.
      if (gen !== devGen.current) { tracks.forEach((t) => { try { t.stop(); } catch (e) { /* gone */ } }); return; }
      tracksRef.current = tracks;
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const list = (k) => all.filter((d) => d.kind === k && d.deviceId).map((d, i) => ({ id: d.deviceId, label: d.label || (k === 'audioinput' ? 'Microphone ' : k === 'videoinput' ? 'Camera ' : 'Speaker ') + (i + 1) }));
        setDevices({ audioinput: list('audioinput'), videoinput: list('videoinput'), audiooutput: list('audiooutput') });
        const id = (k) => { const t = tracks.find((x) => x.kind === (k === 'audioinput' ? Track.Kind.Audio : Track.Kind.Video)); const st = t && t.mediaStreamTrack.getSettings ? t.mediaStreamTrack.getSettings() : {}; return st.deviceId || ''; };
        setChosen((c) => ({ ...c, audioinput: id('audioinput'), videoinput: id('videoinput') }));
      } catch (e) { /* lists stay empty */ }
      setDevicesReady(true);
      const v = tracks.find((t) => t.kind === Track.Kind.Video);
      if (v && previewRef.current) v.attach(previewRef.current);
      const a = tracks.find((t) => t.kind === Track.Kind.Audio);
      const ac = audioContext();
      if (a && ac) {
        const an = ac.createAnalyser(); an.fftSize = 1024;
        ac.createMediaStreamSource(new MediaStream([a.mediaStreamTrack])).connect(an);
        micAnalyser.current = an;
      }
    } catch (e) {
      const name = (e && e.name) || '';
      if (gen !== devGen.current) return;
      setDeviceError(name === 'NotAllowedError' || name === 'PermissionDeniedError'
        ? 'Your browser blocked the camera or microphone. Allow both for this page (look for the camera icon in the address bar), then try again.'
        : name === 'NotFoundError' || name === 'DevicesNotFoundError'
          ? 'We could not find a camera or microphone. Plug one in, or switch to a device that has both, then try again.'
          : name === 'NotReadableError'
            ? 'Another app is using your camera or microphone. Close it (for example a video call), then try again.'
            : 'We could not start your camera and microphone. Try again, or use a different browser.');
    }
  }, [audioContext, stopTracks]);

  useEffect(() => {
    if (stage === 'check') startDevices();
  }, [stage, startDevices]);

  function pickDevice(kind, id) {
    const next = { ...chosen, [kind]: id };
    setChosen(next);
    if (kind !== 'audiooutput') startDevices(next);
  }

  // A short two-note chime through the chosen speaker.
  async function testSpeakers() {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC || tone) return;
    setTone(true);
    try {
      const ac = new AC();
      const dest = ac.createMediaStreamDestination();
      const el = new Audio();
      el.srcObject = dest.stream;
      if (chosen.audiooutput && el.setSinkId) { try { await el.setSinkId(chosen.audiooutput); } catch (e) { /* default speaker */ } }
      await el.play().catch(() => {});
      [[660, 0], [880, 0.35]].forEach(([f, at]) => {
        const o = ac.createOscillator(); const g = ac.createGain();
        o.type = 'sine'; o.frequency.value = f;
        g.gain.setValueAtTime(0, ac.currentTime + at);
        g.gain.linearRampToValueAtTime(0.35, ac.currentTime + at + 0.03);
        g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + at + 0.6);
        o.connect(g); g.connect(dest);
        o.start(ac.currentTime + at); o.stop(ac.currentTime + at + 0.65);
      });
      setTimeout(() => { try { el.pause(); ac.close(); } catch (e) { /* closed */ } setTone(false); }, 1300);
    } catch (e) { setTone(false); }
  }

  useEffect(() => () => {
    // Leaving the page entirely.
    try { if (roomRef.current) roomRef.current.disconnect(); } catch (e) { /* closing */ }
    stopTracks();
  }, [stopTracks]);

  async function giveConsent() {
    setBusy(true);
    try { setInfo(await call(token, '/consent', { agree: true, clips: clipsOk })); setStage('check'); } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  // ── Recording ──────────────────────────────────────────────────────────
  function upload(blob) {
    const u = uploadRef.current;
    const seq = u.seq; u.seq += 1;
    const session = u.session; const mime = u.mime;
    u.queue = u.queue.then(async () => {
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const res = await fetch(API_BASE + '/api/public/interviews/' + token + '/recording?session=' + session + '&seq=' + seq + '&mime=' + encodeURIComponent(mime || ''), {
            method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: blob,
          });
          if (res.ok || res.status === 409 || res.status === 413) return;
        } catch (e) { /* retry */ }
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
    });
  }
  // The recording's sound: the candidate's microphone and Nathan's voice, mixed.
  // Made before the call connects so Nathan's first words are in it.
  function prepareMix() {
    const ac = audioContext();
    mixRef.current = null;
    if (!ac) return;
    const dest = ac.createMediaStreamDestination();
    const a = tracksRef.current.find((t) => t.kind === Track.Kind.Audio);
    if (a) ac.createMediaStreamSource(new MediaStream([a.mediaStreamTrack])).connect(dest);
    mixRef.current = dest;
  }
  function startRecording(session) {
    const mime = pickMime();
    const dest = mixRef.current;
    const v = tracksRef.current.find((t) => t.kind === Track.Kind.Video);
    if (!mime || !dest || !v) return;
    uploadRef.current = { session, seq: 0, queue: Promise.resolve(), mime };
    try {
      const rec = new MediaRecorder(new MediaStream([v.mediaStreamTrack].concat(dest.stream.getAudioTracks())),
        { mimeType: mime, videoBitsPerSecond: 280000, audioBitsPerSecond: 48000 });
      rec.ondataavailable = (e) => { if (e.data && e.data.size) upload(e.data); };
      rec.start(PIECE_MS);
      recRef.current = rec;
    } catch (e) { recRef.current = null; }
  }
  async function stopRecording() {
    const rec = recRef.current;
    recRef.current = null;
    if (rec && rec.state !== 'inactive') {
      await new Promise((resolve) => {
        rec.addEventListener('stop', resolve, { once: true });
        try { rec.stop(); } catch (e) { resolve(); }
        setTimeout(resolve, 3000);
      });
    }
    await Promise.race([uploadRef.current.queue, new Promise((r) => setTimeout(r, 20000))]);
  }

  // ── The call ───────────────────────────────────────────────────────────
  // Pure updater: React may run it twice in development.
  function addSegment(id, who, text, final) {
    setSegments((prev) => {
      const at = prev.findIndex((x) => x.id === id);
      if (at === -1) return prev.concat([{ id, who, text, final }]);
      const next = prev.slice();
      next[at] = { ...next[at], text, final: final || next[at].final };
      return next;
    });
  }

  function readAgent(p) {
    const at = p.attributes || {};
    if (at['lk.agent.state']) setAgentState(at['lk.agent.state']);
    if (at['gt.question']) setProgress({ q: Number(at['gt.question']) || 0, total: Number(at['gt.total']) || 0 });
  }

  function goFullscreen() {
    const el = document.documentElement;
    try { if (!document.fullscreenElement && el.requestFullscreen) el.requestFullscreen().catch(() => {}); } catch (e) { /* not supported, phones */ }
  }

  async function join() {
    goFullscreen();
    strikes.current = 0; setStrikeWarn(false); setNeedFs(false);
    setBusy(true); setError(''); setNathanLate(false);
    audioContext();
    try {
      const j = await call(token, '/join', { name: info && info.first_name });
      prepareMix();
      const room = new Room({ adaptiveStream: true, dynacast: true, ...(chosen.audiooutput ? { audioOutput: { deviceId: chosen.audiooutput } } : {}) });
      roomRef.current = room;
      leavingRef.current = null;

      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind !== Track.Kind.Audio) return;
        const el = track.attach();
        if (audioBoxRef.current) audioBoxRef.current.appendChild(el);
        const ac = audioContext();
        if (ac) {
          const src = ac.createMediaStreamSource(new MediaStream([track.mediaStreamTrack]));
          const an = ac.createAnalyser(); an.fftSize = 1024;
          src.connect(an);
          nathanAnalyser.current = an;
          if (mixRef.current) src.connect(mixRef.current);
        }
      });
      room.on(RoomEvent.TrackUnsubscribed, (track) => { track.detach().forEach((el) => el.remove()); });
      room.on(RoomEvent.ParticipantConnected, (p) => { setNathanLate(false); readAgent(p); });
      room.on(RoomEvent.ParticipantAttributesChanged, (changed, p) => { if (p && p !== room.localParticipant) readAgent(p); });
      room.on(RoomEvent.AudioPlaybackStatusChanged, () => setNeedsAudio(!room.canPlaybackAudio));
      room.registerTextStreamHandler(TOPIC, async (reader, from) => {
        const attrs = (reader.info && reader.info.attributes) || {};
        const id = attrs['lk.segment_id'] || (reader.info && reader.info.id);
        const who = String((from && from.identity) || '').indexOf('cand-') === 0 ? 'you' : 'nathan';
        let text = '';
        try {
          for await (const chunk of reader) {
            text += chunk;
            addSegment(id, who, text, false);
          }
        } catch (e) { /* stream cut short */ }
        addSegment(id, who, text, attrs['lk.transcription_final'] === 'true' || who === 'nathan');
      });
      room.on(RoomEvent.Disconnected, async () => {
        nathanAnalyser.current = null;
        await stopRecording();
        stopTracks();
        const why = leavingRef.current;
        const v = await load();
        if (v && v.status === 'completed') setStage('done');
        else if (why === 'paused') setStage('paused');
        else if (why === 'left_window') setStage('left');
        else if (v && (v.status === 'expired' || v.status === 'cancelled')) setStage('closed');
        else setStage('dropped');
      });

      await room.connect(j.url, j.token);
      for (const t of tracksRef.current) {
        await room.localParticipant.publishTrack(t, t.kind === Track.Kind.Audio ? { dtx: false } : { simulcast: false });
      }
      try { await room.startAudio(); } catch (e) { /* asked for below */ }
      setNeedsAudio(!room.canPlaybackAudio);
      room.remoteParticipants.forEach(readAgent);
      setSegments([]); setElapsed(0); setMuted(false);
      startRecording(j.session);
      setStage('call');
      setTimeout(() => { if (roomRef.current === room && room.remoteParticipants.size === 0) setNathanLate(true); }, 25000);
    } catch (e) {
      setError(e.message || 'We could not start the call.');
      try { if (roomRef.current) await roomRef.current.disconnect(); } catch (e2) { /* never connected */ }
    } finally { setBusy(false); }
  }

  async function toggleMute() {
    const a = tracksRef.current.find((t) => t.kind === Track.Kind.Audio);
    if (!a) return;
    if (muted) await a.unmute(); else await a.mute();
    setMuted(!muted);
  }
  async function repeat() {
    try { await roomRef.current.localParticipant.sendText('Could you repeat the question, please?', { topic: 'lk.chat' }); } catch (e) { /* not connected */ }
  }
  async function leave(reason) {
    setConfirmEnd(false);
    setStrikeWarn(false); setNeedFs(false);
    leavingRef.current = reason;
    try { if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {}); } catch (e) { /* fine */ }
    try { await call(token, '/leave', { reason }); } catch (e) { /* the disconnect still happens */ }
    try { await roomRef.current.disconnect(); } catch (e) { /* already gone */ }
  }

  // Staying in the window. The moment the candidate leaves the tab, or leaves
  // the window for more than three seconds, the interview pauses: their
  // microphone is muted and Nathan stops and asks them to come back, so nothing
  // can be answered from another screen. The first time is a warning; the
  // second ends the interview. Leaving full screen asks them to go back.
  // Every one is recorded for the reviewer.
  const leaveRef = useRef(null);
  leaveRef.current = leave;
  const mutedRef = useRef(false);
  mutedRef.current = muted;
  function sendFlag(body) {
    fetch(API_BASE + '/api/public/interviews/' + token + '/flag', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).catch(() => {});
  }
  function setFocus(state) {
    const room = roomRef.current;
    try { if (room && room.localParticipant) room.localParticipant.setAttributes({ 'gt.focus': state }).catch(() => {}); } catch (e) { /* not connected */ }
    const a = tracksRef.current.find((t) => t.kind === Track.Kind.Audio);
    try { if (a) { if (state === 'away') a.mute(); else if (!mutedRef.current) a.unmute(); } } catch (e) { /* track gone */ }
  }
  function comeBack() {
    if (awaySince.current) sendFlag({ kind: 'returned', seconds: Math.round((Date.now() - awaySince.current) / 1000) });
    awaySince.current = 0;
    setStrikeWarn(false);
    setFocus('here');
    goFullscreen();
  }
  useEffect(() => {
    if (stage !== 'call') return undefined;
    let blurTimer = null;
    function away(kind) {
      if (leavingRef.current || awaySince.current) return;
      awaySince.current = Date.now();
      strikes.current += 1;
      sendFlag({ kind, strike: strikes.current });
      if (strikes.current >= 2) { leaveRef.current('left_window'); return; }
      setFocus('away');
      setStrikeWarn(true);
    }
    function onVis() {
      if (document.visibilityState === 'hidden') { clearTimeout(blurTimer); blurTimer = null; away('left_tab'); }
    }
    function onBlur() {
      if (document.visibilityState === 'hidden') return;
      clearTimeout(blurTimer);
      blurTimer = setTimeout(() => { blurTimer = null; if (!document.hasFocus()) away('left_window'); }, 3000);
    }
    function onFocus() { if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; } }
    function onFs() {
      if (!document.fullscreenElement && !leavingRef.current && document.fullscreenEnabled) {
        setNeedFs(true);
        sendFlag({ kind: 'left_fullscreen' });
      } else if (document.fullscreenElement) setNeedFs(false);
    }
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    document.addEventListener('fullscreenchange', onFs);
    return () => {
      clearTimeout(blurTimer);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('fullscreenchange', onFs);
    };
  }, [stage, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = info ? info.title : '';
  const mins = info ? info.minutes : 25;

  if (stage === 'loading') return <Shell><div className="gtir-card gtir-loading">Loading your interview…</div></Shell>;
  if (stage === 'gone') {
    return <Message heading="This link is not valid"><p>Check you opened the whole link from your email. If it still does not work, reply to that email and a person will help.</p></Message>;
  }
  if (stage === 'error') {
    return <Message heading="Something went wrong" action={<button type="button" className="gtir-btn gtir-btn--go" onClick={() => window.location.reload()}>Try again</button>}><p>{error}</p></Message>;
  }
  if (stage === 'closed') {
    return (
      <Message title={title} heading="This interview has closed">
        <p>The link has expired or the interview was withdrawn. If you would still like to take part, reply to the email we sent you and a person will help.</p>
      </Message>
    );
  }
  if (stage === 'left') {
    return (
      <Message title={title} heading="The interview has ended">
        <p>The interview ended because you left the interview window a second time. What you said up to that point has been saved and will be reviewed by a person at GTCO.</p>
        <p className="gtir-muted">If something went wrong on your side, reply to the email we sent you and explain. A person will read it.</p>
      </Message>
    );
  }
  if (stage === 'done') {
    return (
      <Message title={title} heading="Thank you, that is everything">
        <p>Your interview is complete. A person at GTCO reviews every interview, and you will hear about next steps by email.</p>
        <p className="gtir-muted">You can close this page.{info && info.is_test ? ' This was a test run: nothing is reported on it.' : ''}</p>
      </Message>
    );
  }
  if (stage === 'paused' || stage === 'dropped') {
    return (
      <Message title={title} heading={stage === 'paused' ? 'Your progress is saved' : 'The call dropped'}
        action={<button type="button" className="gtir-btn gtir-btn--go" onClick={() => setStage('check')}>{stage === 'paused' ? 'Carry on now' : 'Rejoin the interview'}</button>}>
        <p>{stage === 'paused'
          ? 'You can come back to this link at any time' + (info && info.expires_at ? ' before ' + ukDay(info.expires_at) : '') + '. Nathan will pick up where you left off.'
          : 'Nothing is lost. Rejoin and Nathan will pick up where you left off.'}</p>
      </Message>
    );
  }

  if (stage === 'notice') {
    return (
      <Shell title={title}>
        <section className="gtir-card gtir-notice">
          <div className="gtir-hello">
            <span className="gtir-orbmini" aria-hidden="true" />
            <div>
              <p className="gtir-eyebrow">{info.is_test ? 'Test run' : 'Your interview'}</p>
              <h1>{info.first_name ? 'Hello ' + info.first_name + ', meet Nathan' : 'Meet Nathan'}</h1>
            </div>
          </div>
          <p className="gtir-lede">Nathan is Generative Talent Co's AI interviewer. He will talk with you for about {mins} minutes about your experience for <b>{title}</b>.</p>
          <ul className="gtir-points">
            <li><b>He is artificial intelligence, not a person.</b> He asks the questions and listens. He does not make decisions about you.</li>
            <li><b>A person reviews every interview.</b> Someone at GTCO watches or reads it before anything happens next. You can ask for a person to look again at any decision.</li>
            <li><b>The call is recorded and transcribed.</b> Your camera and both voices are recorded, and the conversation is written down as you go, so it can be reviewed fairly.</li>
            <li><b>Take your time.</b> {info.questions ? 'There are ' + info.questions + ' topics. ' : ''}Pause to think whenever you like, and ask him to repeat a question. If the call drops, open this link again to carry on.</li>
            <li><b>Answer in your own words.</b> He asks about your own work, so there are no right answers to look up.</li>
            <li className="gtir-point--rule"><b>Stay in the interview window.</b> The interview runs in full screen. Do not open another tab, window or app until you have finished. If you leave, the interview pauses and Nathan waits for you, and you get one warning. If you leave a second time, the interview ends and goes for review as it stands.</li>
          </ul>
          <p className="gtir-fine">GTCO Ltd is responsible for your data. We keep the recording and transcript only as long as we need them for recruitment, and you can ask us to delete them from your portal or by replying to our email. Deleting them does not remove anything already shared with an employer.</p>
          <label className="gtir-agree">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
            <span>I understand that Nathan is artificial intelligence, I agree to the interview being recorded and transcribed for GTCO to review, and I will stay in the interview window until it ends.</span>
          </label>
          <label className="gtir-agree gtir-agree--opt">
            <input type="checkbox" checked={clipsOk} onChange={(e) => setClipsOk(e.target.checked)} />
            <span><b>Optional.</b> I am happy for short clips of my answers to be shared with employers GTCO puts me forward to. GTCO chooses the clips, and only employers who can already see my name will see them. Leaving this unticked does not affect your interview.</span>
          </label>
          {error && <p className="gtir-err" role="alert">{error}</p>}
          <div className="gtir-actions">
            <button type="button" className="gtir-btn gtir-btn--go" disabled={!agree || busy} onClick={giveConsent}>{busy ? 'One moment…' : 'Continue'}</button>
          </div>
        </section>
      </Shell>
    );
  }

  if (stage === 'check') {
    return (
      <Shell title={title}>
        <section className="gtir-card gtir-check">
          <div className="gtir-check-v">
            <video ref={previewRef} className="gtir-preview" autoPlay muted playsInline />
            {!devicesReady && !deviceError && <span className="gtir-preview-wait">Starting your camera…</span>}
          </div>
          <div className="gtir-check-t">
            <p className="gtir-eyebrow">Before you start</p>
            <h1>Check you can be seen and heard</h1>
            <div className="gtir-dev">
              <div className="gtir-dev-row">
                <span className="gtir-dev-k">Camera</span>
                <select className="gtir-sel" aria-label="Camera" value={chosen.videoinput} onChange={(e) => pickDevice('videoinput', e.target.value)} disabled={!devices.videoinput.length}>
                  {devices.videoinput.length ? devices.videoinput.map((d) => <option key={d.id} value={d.id}>{d.label}</option>) : <option value="">Default camera</option>}
                </select>
              </div>
              <div className="gtir-dev-row">
                <span className="gtir-dev-k">Microphone</span>
                <select className="gtir-sel" aria-label="Microphone" value={chosen.audioinput} onChange={(e) => pickDevice('audioinput', e.target.value)} disabled={!devices.audioinput.length}>
                  {devices.audioinput.length ? devices.audioinput.map((d) => <option key={d.id} value={d.id}>{d.label}</option>) : <option value="">Default microphone</option>}
                </select>
                <div className="gtir-dev-meter"><MicMeter levelRef={micLevel} muted={false} /><span>Say something: the bars should move</span></div>
              </div>
              <div className="gtir-dev-row">
                <span className="gtir-dev-k">Speaker</span>
                {devices.audiooutput.length > 0 && (
                  <select className="gtir-sel" aria-label="Speaker" value={chosen.audiooutput} onChange={(e) => pickDevice('audiooutput', e.target.value)}>
                    <option value="">System default</option>
                    {devices.audiooutput.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </select>
                )}
                <button type="button" className="gtir-btn gtir-btn--sm" onClick={testSpeakers} disabled={tone}>{tone ? 'Playing…' : 'Play a test sound'}</button>
              </div>
            </div>
            <p className="gtir-muted">Somewhere quiet, face lit from the front. Headphones help.</p>
            {deviceError && <p className="gtir-err" role="alert">{deviceError} <button type="button" className="gtir-link" onClick={() => startDevices(chosen)}>Try again</button></p>}
            {!info.live && <p className="gtir-err" role="alert">Live interviews are not switched on yet. Please try again later.</p>}
            {error && <p className="gtir-err" role="alert">{error}</p>}
            <div className="gtir-actions">
              <button type="button" className="gtir-btn gtir-btn--go" disabled={busy || !!deviceError || !info.live || !devicesReady} onClick={join}>
                {busy ? 'Connecting to Nathan…' : info.started ? 'Continue the interview' : 'Start the interview'}
              </button>
            </div>
            <p className="gtir-muted">About {mins} minutes. You can pause and come back to this link.</p>
          </div>
        </section>
      </Shell>
    );
  }

  // stage === 'call'
  const latest = segments.length ? segments[segments.length - 1] : null;
  const stateText = agentState === 'speaking' ? 'Speaking' : agentState === 'thinking' ? 'Thinking' : agentState === 'initializing' ? 'Joining' : 'Listening';
  return (
    <div className="gtir gtir--call">
      <div className="gtir-wrap gtir-wrap--wide">
        <header className="gtir-top">
          <Brand title={title} />
          <div className="gtir-meta">
            <span className="gtir-chip"><i className="gtir-rec" aria-hidden="true" />Recording</span>
            {progress.total > 0 && <span className="gtir-chip">Topic <b>{progress.q}</b> of <b>{progress.total}</b></span>}
            <span className="gtir-chip"><b>{clock(elapsed)}</b></span>
            <button type="button" className="gtir-chip gtir-chip--btn gtir-only-narrow" onClick={() => setShowTx(!showTx)}>{showTx ? 'Hide transcript' : 'Transcript'}</button>
          </div>
        </header>

        <div className={'gtir-main' + (showTx ? ' gtir-main--tx' : '')}>
          <section className="gtir-stage" aria-label="Interview call">
            <div className="gtir-orbwrap">
              <Orb levelRef={nathanLevel} modeRef={modeRef} />
              <div className="gtir-who">
                <b>Nathan</b>
                <span>AI interviewer</span>
                <div><span className={'gtir-state gtir-state--' + agentState}><i />{stateText}</span></div>
              </div>
            </div>
            <div className="gtir-captions" aria-live="polite">
              {nathanLate ? (
                <p className="gtir-cap-note">Nathan is taking longer than usual to join. Wait a moment, or pause and try again in a few minutes.</p>
              ) : latest ? (
                <>
                  <span className={'gtir-speaker' + (latest.who === 'you' ? ' gtir-speaker--me' : '')}>{latest.who === 'you' ? 'You' : 'Nathan'}</span>
                  <p>{latest.text}</p>
                </>
              ) : (
                <p className="gtir-cap-note">Nathan is joining. He will say hello in a moment.</p>
              )}
            </div>
            <div className="gtir-self" aria-label="Your camera">
              <video ref={(el) => { selfRef.current = el; const v = tracksRef.current.find((t) => t.kind === Track.Kind.Video); if (el && v && !el.srcObject) v.attach(el); }} autoPlay muted playsInline />
              <span className="gtir-self-lab">You{muted ? ' (muted)' : ''}</span>
              <MicMeter levelRef={micLevel} muted={muted} />
            </div>
            {needsAudio && (
              <button type="button" className="gtir-sound" onClick={() => roomRef.current && roomRef.current.startAudio().then(() => setNeedsAudio(false)).catch(() => {})}>Tap to hear Nathan</button>
            )}
            <div className="gtir-bar" role="toolbar" aria-label="Call controls">
              <button type="button" onClick={toggleMute} aria-pressed={muted}>{muted ? 'Unmute' : 'Mute'}</button>
              <button type="button" onClick={repeat}>Repeat question</button>
              <button type="button" className="gtir-end" onClick={() => setConfirmEnd(true)}>Leave</button>
            </div>
          </section>

          <aside className="gtir-tx" aria-label="Transcript">
            <header><b>Transcript</b><span className="gtir-live"><i />Live</span></header>
            <div className="gtir-txlist">
              {segments.length === 0 && <p className="gtir-muted">What you and Nathan say appears here as you talk.</p>}
              {segments.map((s) => (
                <div key={s.id} className={'gtir-turn gtir-turn--' + s.who + (s.final ? '' : ' gtir-turn--live')}>
                  <b>{s.who === 'you' ? 'You' : 'Nathan'}</b>
                  <p>{s.text}</p>
                </div>
              ))}
              <div ref={txEndRef} />
            </div>
          </aside>
        </div>
      </div>
      <div ref={audioBoxRef} hidden />

      {strikeWarn && (
        <div className="gtir-modal-bg">
          <div className="gtir-modal gtir-modal--warn" role="alertdialog" aria-modal="true" aria-label="Warning">
            <h2>The interview is paused</h2>
            <p>You left the interview window, so Nathan has stopped and your microphone is off. Please stay on this screen until the interview ends. This is your only warning: if you leave the window again, the interview will end and go for review as it stands.</p>
            <div className="gtir-modal-acts">
              <button type="button" className="gtir-btn gtir-btn--go" onClick={comeBack}>Back to the interview</button>
            </div>
          </div>
        </div>
      )}

      {needFs && !strikeWarn && (
        <div className="gtir-modal-bg">
          <div className="gtir-modal" role="alertdialog" aria-modal="true" aria-label="Full screen">
            <h2>Go back to full screen</h2>
            <p>The interview runs in full screen. Nathan is still with you.</p>
            <div className="gtir-modal-acts">
              <button type="button" className="gtir-btn gtir-btn--go" onClick={() => { setNeedFs(false); goFullscreen(); }}>Return to full screen</button>
            </div>
          </div>
        </div>
      )}

      {confirmEnd && (
        <div className="gtir-modal-bg" onClick={() => setConfirmEnd(false)}>
          <div className="gtir-modal" role="dialog" aria-modal="true" aria-label="Leave the interview" onClick={(e) => e.stopPropagation()}>
            <h2>Leave the interview?</h2>
            <p>If you pause, your progress is saved and you can carry on from this link later. If you end it, what you have said so far is sent for review and you cannot add more.</p>
            <div className="gtir-modal-acts">
              <button type="button" className="gtir-btn" onClick={() => setConfirmEnd(false)}>Keep going</button>
              <button type="button" className="gtir-btn" onClick={() => leave('paused')}>Pause and come back</button>
              <button type="button" className="gtir-btn gtir-btn--end" onClick={() => leave('ended')}>End the interview</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
